import { BaseComponent } from '../../base-component';
import { ArticleOptionsServices } from '../../global';
import { ARTICLE_OPTIONS_CHANGED_EVENT } from './events';
import { eventQBus } from '@otto-ec/global-resources/event-q-bus';
import { unsetAll, withEvents } from '@otto-ec/global-resources/event';
import { OcSheetV1Events, sheetV1 } from '@otto-ec/otto-components/sheet';

export class ArticleOptionsComponent extends BaseComponent {
    public rootSelector = '.js_pdp_article-options';
    private deliveryOnDemand = false;

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            /*                                                */
            this.emitEmptyCheckboxesState();
            return;
        }
        this.deliveryOnDemand = this.getRootElement().dataset.variationDeliveryOnDemand === 'true';

        /*                                                                */
        this.emitSelectedCheckboxesState();

        this.getChildElements('.js_pdp_article-options__item').forEach(this.initializeItem);
    }

    private initializeItem = (itemElement: HTMLElement) => {
        const confirmationInSheetRequired = itemElement.dataset.confirmationInSheetRequired === 'true';
        const encodedContentUrl = itemElement.dataset.encodedContentUrl || '';
        const detailsLinkElement = itemElement.querySelector<HTMLOcLinkV2Element>('.js_pdp_article-options__details');
        const checkboxElement = itemElement.querySelector<HTMLInputElement>('.js_pdp_article-options__checkbox-input');

        if (detailsLinkElement && checkboxElement) {
            /*                                                                       */
            this.updateWarningState(checkboxElement);

            if (confirmationInSheetRequired) {
                checkboxElement.addEventListener('click', e => {
                    /*                                                           */
                    /*                                                           */
                    if (checkboxElement.checked) {
                        this.openSheet(encodedContentUrl, confirmationInSheetRequired, checkboxElement);
                        /*                                                                                              */
                        e.preventDefault();
                    }
                });
            }

            checkboxElement.addEventListener('change', () => this.onChkBoxChanged(checkboxElement));

            detailsLinkElement.addEventListener('click', () => {
                this.openSheet(encodedContentUrl, confirmationInSheetRequired, checkboxElement);
            });
        }
    };

    private async openSheet(encodedContentUrl: string, withConfirmation: boolean, checkboxElement: HTMLInputElement) {
        const sheet = await sheetV1.create({
            id: 'pdp_article-options__content-sheet',
            base64Url: encodedContentUrl,
        });

        if (withConfirmation) {
            const on = withEvents<OcSheetV1Events>(sheet);
            const unset = unsetAll(
                on('oc-sheet-content-parse', evt => {
                    evt.detail.parsed.appendChild(this.createActionSlotElement(checkboxElement, sheet));
                    unset();
                }),
                on('oc-sheet-close', () => unset())
            );
        }

        sheet.open = true;
    }

    private createActionSlotElement(checkboxElement: HTMLInputElement, sheetElement: HTMLOcSheetV1Element): HTMLDivElement {
        const actionSlotElement = document.createElement('div');
        actionSlotElement.slot = 'actions';

        const submitButtonElement = document.createElement('oc-button-v1');
        submitButtonElement.variant = 'primary';
        submitButtonElement.textContent = 'Hinzufügen';
        submitButtonElement.addEventListener('click', () => {
            checkboxElement.checked = true;
            this.onChkBoxChanged(checkboxElement);
            sheetElement.close();
        });

        const cancelButtonElement = document.createElement('oc-button-v1');
        cancelButtonElement.variant = 'secondary';
        cancelButtonElement.className = 'oc-mt-50';
        cancelButtonElement.textContent = 'Ohne fortfahren';
        cancelButtonElement.addEventListener('click', () => {
            checkboxElement.checked = false;
            this.onChkBoxChanged(checkboxElement);
            sheetElement.close();
        });

        actionSlotElement.append(submitButtonElement, cancelButtonElement);
        return actionSlotElement;
    }

    private onChkBoxChanged(chkBox: HTMLInputElement) {
        this.updateWarningState(chkBox);
        this.processTracking(chkBox);
        this.emitSelectedCheckboxesState();
        this.handleDeliveryOnDemand();
    }

    private processTracking(chkBox: HTMLInputElement) {
        const notApplicableForDesiredDate = chkBox.dataset.deliveryForDesiredDate === 'false';
        const trackSlowFeature = chkBox.checked && this.deliveryOnDemand && notApplicableForDesiredDate;
        const serviceId = this.getServiceIdOfCheckBox(chkBox);

        if (trackSlowFeature) {
            const availabilityMessageSlow = this.getRootElement().dataset.availabilityMessageSlow;
            this.trackEvent({
                order_Service: this.getLabelOfCheckBox(chkBox),
                order_ServiceActivity: 'select',
                product_AvailabilityView: availabilityMessageSlow,
                order_ServiceId: serviceId,
                product_ServiceCount: this.getAvailableArticleOptions(),
            });
        } else {
            this.trackEvent({
                order_Service: this.getLabelOfCheckBox(chkBox),
                order_ServiceActivity: chkBox.checked ? 'select' : 'deselect',
                order_ServiceId: serviceId,
                product_ServiceCount: this.getAvailableArticleOptions(),
            });
        }
    }

    /**
 *
 */
    private emitSelectedCheckboxesState(): void {
        eventQBus.emit(ARTICLE_OPTIONS_CHANGED_EVENT, this.getSelectedServicesForOrdering());
    }

    /**
 *
 */
    private emitEmptyCheckboxesState(): void {
        eventQBus.emit(ARTICLE_OPTIONS_CHANGED_EVENT, this.createServicesChangedEvent([]));
    }

    private getCheckBoxes(): HTMLInputElement[] {
        return this.getChildElements<HTMLInputElement>('.js_pdp_article-options__checkbox-input');
    }

    private updateWarningState(chkBox: HTMLInputElement): void {
        const notApplicableForDesiredDate = chkBox.dataset.deliveryForDesiredDate === 'false';
        const showWarning = chkBox.checked && this.deliveryOnDemand && notApplicableForDesiredDate;
        this.setWarningVisibility(chkBox, showWarning);
    }

    private setWarningVisibility(chkBox: HTMLInputElement, shouldShowWarning: boolean): void {
        const warningElem = chkBox.parentElement
            ?.closest('.js_pdp_article-options__item')
            ?.querySelector<HTMLDivElement>('.js_pdp_article-options__warning');
        if (!warningElem) {
            throw new Error('cant find warning element for input' + chkBox.id);
        }

        if (shouldShowWarning) {
            warningElem.classList.remove('pl_hidden');
        } else {
            warningElem.classList.add('pl_hidden');
        }
    }

    private getLabelOfCheckBox(chkBox: HTMLInputElement): string {
        return chkBox.closest('oc-checkbox-v2')?.querySelector('[slot="label"]')?.textContent || '';
    }

    private getServiceIdOfCheckBox(chkBox: HTMLInputElement): string {
        return chkBox.getAttribute('data-id') || '';
    }

    private getSelectedServicesForOrdering(): ArticleOptionsServices {
        const serviceUuids = this.getCheckBoxes()
            .filter(chkBox => chkBox.checked)
            .map(chkBox => chkBox.dataset.id || '');

        return this.createServicesChangedEvent(serviceUuids);
    }

    private getAvailableArticleOptions(): number {
        return this.getCheckBoxes().length;
    }

    private createServicesChangedEvent(serviceUuids: string[]): { services: { uuid: string }[] } {
        const serviceDefinitions = serviceUuids.map(uuid => ({ uuid: uuid }));
        return { services: serviceDefinitions };
    }

    /*                                                   */
    private handleDeliveryOnDemand(): void {
        const dodAvailable =
            this.getCheckBoxes()
                .filter(chkBox => chkBox.checked)
                .map(chkBox => chkBox.dataset.deliveryForDesiredDate)
                .filter(state => state === 'false').length == 0;

        if (dodAvailable) {
            /*                                                   */
            const inactive = document.querySelector('.js_pdp_delivery__dod-wrapper__inactive');
            const active = document.querySelector('.js_pdp_delivery__dod-wrapper__active');
            if (inactive && active) {
                inactive.classList.add('pl_hidden');
                active.classList.remove('pl_hidden');
            }
        } else {
            /*                                                   */
            const inactive = document.querySelector('.js_pdp_delivery__dod-wrapper__inactive');
            const active = document.querySelector('.js_pdp_delivery__dod-wrapper__active');
            if (inactive && active) {
                inactive.classList.remove('pl_hidden');
                active.classList.add('pl_hidden');
            }
        }
    }
}
